import {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {diApi} from '@di-shared/api/di';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {ClickAwayListener} from '@mui/base';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface VehicleInspectionContentsProps {
  isAgree: boolean;
  puid: string;
  vin: string;
}

export enum InspectionReservationStatus {
  PAYMENT_COMPLETE, // 1. 결제 완료
  RESERVATION_COMPLETE, // 2. 예약 완료
  RESERVABLE, // 3. 검사 예약 대상자
  NOT_RESERVABLE_REGISTERED_DATE, // 4. 검사 예약 미대상자 && 검사일 등록함
  NOT_RESERVABLE_NO_REGISTERED_DATE, // 5. 검사 예약 미대상자 && 검사일 미등록
}

interface GetInspectionReservationStatusParams {
  isPaid: boolean;
  isReserved: boolean;
  isReservationAvailable: boolean;
  reservationPeriodStartDate: string;
  reservationPeriodEndDate: string;
}

function getInspectionReservationStatus({
  isPaid,
  isReserved,
  isReservationAvailable,
  reservationPeriodEndDate,
  reservationPeriodStartDate,
}: GetInspectionReservationStatusParams): InspectionReservationStatus {
  /**
   * [2025-09-29:Jeyoung Lee]
   * @link https://hmg.dooray.com/project/tasks/4167783805895275295#comment-4167784930623146370
   */
  if (isPaid) {
    return InspectionReservationStatus.PAYMENT_COMPLETE;
  }
  if (isReserved) {
    return InspectionReservationStatus.RESERVATION_COMPLETE;
  }
  if (isReservationAvailable) {
    return InspectionReservationStatus.RESERVABLE;
  }
  if (reservationPeriodEndDate && reservationPeriodStartDate) {
    return InspectionReservationStatus.NOT_RESERVABLE_REGISTERED_DATE;
  }
  return InspectionReservationStatus.NOT_RESERVABLE_NO_REGISTERED_DATE;
}

function VehicleInspectionContents({isAgree, puid, vin}: VehicleInspectionContentsProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<Loading />}>
        {isAgree ? (
          <AgreeInner
            puid={puid}
            vin={vin}
          />
        ) : (
          <DisagreeInner vin={vin} />
        )}
      </Suspense>
    </ErrorBoundary>
  );
}

const MAX_VEHICLE_COUNT = 10;
const MIN_VEHICLE_COUNT = 1;
const MAX_FAIL_COUNT = 10;

function AgreeInner({puid, vin}: Omit<VehicleInspectionContentsProps, 'isAgree'>) {
  const {t} = useInferableTranslation();
  const {resolveError} = useApiError();
  const {openConfirm, openAlert} = useCcWebviewUtilsHelper();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const {data, refetch: refetchReservationStatus} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.publicServiceInspection.getReservationStatus), puid],
    queryFn: () => diApi.publicServiceInspection.getReservationStatus({puid}),
    refetchOnWindowFocus: true,
  });

  const [unregisterVehicle] = usePendingHandler(
    async (licensePlateNumber: string) => {
      const isConfirm = await openConfirm({
        title: t('T30542'),
        message: t('T30543'),
        okText: t('T30544'),
        cancelText: t('T10221'),
      });

      if (!isConfirm) {
        return;
      }

      await diApi.publicServiceInspection.unregisterVehicle({
        requestBody: {puid, licensePlateNumber},
      });
      await refetchReservationStatus();
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
    }
  );

  if (data.data.statusList.length === 0) {
    return (
      <li className="application_item asset_after">
        <div className="title_area">
          <strong className="title">{t('T30528')}</strong>
        </div>
        <p className="desc">{t('T30557')}</p>
        <div className="btn_box">
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            onClick={async () => {
              try {
                const {data} = await diApi.publicServiceInspection.getTodayFailCount({puid});
                if (data.authFailCount >= MAX_FAIL_COUNT) {
                  await openAlert({
                    title: t('T30546'),
                    message: t('T30547'),
                    okText: t('T10221'),
                  });
                  return;
                }
                WebviewUtils.openNewWebView({
                  url: window.location.origin + ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_NEW.getFullPath(vin),
                  isModal: true,
                  isNavigationBar: false,
                  isToolBar: false,
                });
              } catch (e) {
                const [execute] = await resolveError(e);
                execute();
              }
            }}
          >
            {t('T30558')}
          </button>
        </div>
      </li>
    );
  }
  return (
    <li className="application_item asset_after">
      <div className="title_area">
        <strong className="title">{t('T30528')}</strong>
        <button
          type="button"
          className="btn_view"
          onClick={() => setIsMenuOpen(true)}
        >
          <span className="ic ic_more_vertical_16px"></span>
        </button>
        {isMenuOpen && (
          <ClickAwayListener
            scrollEvent
            mouseEvent="onMouseDown"
            touchEvent="onTouchStart"
            onClickAway={() => setIsMenuOpen(false)}
          >
            <div className="vehicle_floating_button_area">
              <button
                className="vehicle_btn_item"
                onClick={async () => {
                  setIsMenuOpen(false);
                  if (data.data.statusList.length >= MAX_VEHICLE_COUNT) {
                    await openAlert({
                      title: '',
                      message: t('T30545'),
                    });
                    return;
                  }
                  try {
                    const {data} = await diApi.publicServiceInspection.getTodayFailCount({puid});
                    if (data.authFailCount >= MAX_FAIL_COUNT) {
                      await openAlert({
                        title: t('T30546'),
                        message: t('T30547'),
                        okText: t('T10221'),
                      });
                      return;
                    }
                    WebviewUtils.openNewWebView({
                      url: window.location.origin + ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_ADD.getFullPath(vin),
                      isModal: true,
                      isNavigationBar: false,
                      isToolBar: false,
                    });
                  } catch (e) {
                    const [execute] = await resolveError(e);
                    execute();
                  }
                }}
              >
                <span className="vehicle_btn_text">{t('T30529')}</span>
              </button>
              <button
                className="vehicle_btn_item"
                onClick={async () => {
                  await unregisterVehicle(data.data.statusList[selectedIndex].licensePlateNumber);
                  setIsMenuOpen(false);
                }}
                disabled={data.data.statusList.length <= MIN_VEHICLE_COUNT}
              >
                <span className="vehicle_btn_text">{t('T30530')}</span>
              </button>
            </div>
          </ClickAwayListener>
        )}
      </div>

      <Swiper
        slidesPerView="auto"
        modules={[Pagination]}
        className="application_swiper swiper-autoheight"
        observer
        observeParents
        pagination={{el: '.swiper-pagination', type: 'bullets'}}
        autoHeight
        onSlideChange={(swiper) => {
          setSelectedIndex(swiper.activeIndex);
        }}
        onSwiper={(swiper) => {
          setSelectedIndex(swiper.activeIndex);
        }}
        /**
         * [2025-10-01:Jeyoung Lee] FIXME: swiper 수정되면 사라질 인라인 스타일
         */
        style={{
          marginLeft: 'auto',
          marginRight: 'auto',
          position: 'relative',
          overflow: 'hidden',
          listStyle: 'none',
          zIndex: 1,
          display: 'block',
        }}
      >
        {data.data.statusList.map(
          (
            {
              isPaid,
              isReserved,
              isReservationAvailable,
              reservationPeriodEndDate,
              reservationPeriodStartDate,
              reservationDateTime,
              licensePlateNumber,
              reservationNumber,
            },
            index
          ) => {
            const inspectionReservationStatus = getInspectionReservationStatus({
              isPaid,
              isReserved,
              isReservationAvailable,
              reservationPeriodEndDate,
              reservationPeriodStartDate,
            });
            /**
             * [2025-09-29:Jeyoung Lee] FIXME: new Date 안전하게 파싱
             */
            switch (inspectionReservationStatus) {
              case InspectionReservationStatus.PAYMENT_COMPLETE:
                return (
                  <SwiperSlide key={licensePlateNumber}>
                    <PaymentComplete
                      vin={vin}
                      reservationNumber={reservationNumber}
                      licensePlateNumber={licensePlateNumber}
                      reservationDateTime={parseDateWithFormat(reservationDateTime)}
                    />
                  </SwiperSlide>
                );
              case InspectionReservationStatus.RESERVATION_COMPLETE:
                return (
                  <SwiperSlide key={licensePlateNumber}>
                    <ReservationComplete
                      vin={vin}
                      reservationNumber={reservationNumber}
                      licensePlateNumber={licensePlateNumber}
                      reservationDateTime={parseDateWithFormat(reservationDateTime)}
                    />
                  </SwiperSlide>
                );

              case InspectionReservationStatus.RESERVABLE:
                return (
                  <SwiperSlide key={licensePlateNumber}>
                    <Reservable
                      vin={vin}
                      index={index}
                      licensePlateNumber={licensePlateNumber}
                      reservationPeriodStartDate={new Date(reservationPeriodStartDate)}
                      reservationPeriodEndDate={new Date(reservationPeriodEndDate)}
                    />
                  </SwiperSlide>
                );

              case InspectionReservationStatus.NOT_RESERVABLE_REGISTERED_DATE:
                return (
                  <SwiperSlide key={licensePlateNumber}>
                    <NotReservableRegisteredDate
                      vin={vin}
                      index={index}
                      licensePlateNumber={licensePlateNumber}
                      reservationPeriodStartDate={new Date(reservationPeriodStartDate)}
                      reservationPeriodEndDate={new Date(reservationPeriodEndDate)}
                    />
                  </SwiperSlide>
                );
              case InspectionReservationStatus.NOT_RESERVABLE_NO_REGISTERED_DATE:
              default:
                return (
                  <SwiperSlide key={licensePlateNumber}>
                    <NotReservableNotRegisteredDate
                      vin={vin}
                      licensePlateNumber={licensePlateNumber}
                      index={index}
                    />
                  </SwiperSlide>
                );
            }
          }
        )}

        <div className="swiper-pagination"></div>
      </Swiper>
    </li>
  );
}

interface DisagreeInnerProps {
  vin: string;
}

function DisagreeInner({vin}: DisagreeInnerProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();

  return (
    <li className="application_item">
      <a
        className="link"
        role="button"
        onClick={() => navigate(ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_ABOUT.getFullPath(vin))}
      >
        <div className="application_text">
          <strong className="title">{t('T30528')}</strong>
          <p className="desc">{t('T30531')}</p>
        </div>
        <span className="ic ic_arrow_forward_16px"></span>
      </a>
    </li>
  );
}

export default VehicleInspectionContents;

interface PaymentCompleteProps {
  vin: string;
  reservationNumber: string;
  licensePlateNumber: string;
  reservationDateTime: Date | null;
}

function PaymentComplete({vin, reservationNumber, licensePlateNumber, reservationDateTime}: PaymentCompleteProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  return (
    <>
      <div className="badge_box">
        <span className="badge badge_basic_container">{t('T10265')}</span>
        <span className="badge badge_basic_container">{t('T30533')}</span>
      </div>

      <dl className="vehicle_info">
        <div className="vehicle_item">
          <dt className="info_title">{t('T30520')}</dt>
          <dd className="info_text">{licensePlateNumber}</dd>
        </div>

        <div className="vehicle_item">
          <dt className="info_title">{t('T30534')}</dt>
          {reservationDateTime && <dd className="info_text">{CldrDatetimeFormatter.format(reservationDateTime, 'yMMMaHM')}</dd>}
        </div>
      </dl>

      <div className="btn_box">
        <button
          type="button"
          className="btn btn_large btn_filled_gray btn_full"
          onClick={() => {
            navigate(ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER.getFullPath(vin, reservationNumber));
          }}
        >
          {t('T30535')}
        </button>
      </div>
    </>
  );
}

interface ReservationCompleteProps {
  vin: string;
  reservationNumber: string;
  licensePlateNumber: string;
  reservationDateTime: Date | null;
}

function ReservationComplete({vin, reservationNumber, licensePlateNumber, reservationDateTime}: ReservationCompleteProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  return (
    <>
      <div className="badge_box">
        <span className="badge badge_basic_container">{t('T30532')}</span>
        <span className="badge badge_container_red">{t('T30536')}</span>
      </div>

      <dl className="vehicle_info">
        <div className="vehicle_item">
          <dt className="info_title">{t('T30520')}</dt>
          <dd className="info_text">{licensePlateNumber}</dd>
        </div>

        {
          <div className="vehicle_item">
            <dt className="info_title">{t('T30604')}</dt>
            {reservationDateTime && <dd className="info_text">{CldrDatetimeFormatter.format(reservationDateTime, 'yMMMaHM')}</dd>}
          </div>
        }
      </dl>
      <div className="btn_box">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => {
            WebviewUtils.openNewWebView({
              url:
                window.location.origin +
                ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER_PAYMENT.getFullPath(vin, reservationNumber),
              isModal: true,
              isNavigationBar: false,
              isToolBar: false,
            });
          }}
        >
          {t('T30537')}
        </button>

        <button
          type="button"
          className="btn btn_large btn_filled_gray btn_full"
          onClick={() => {
            navigate(ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_$RESERVATION_NUMBER.getFullPath(vin, reservationNumber));
          }}
        >
          {t('T30535')}
        </button>
      </div>
    </>
  );
}

interface ReservableProps {
  vin: string;
  index: number;
  licensePlateNumber: string;
  reservationPeriodStartDate: Date;
  reservationPeriodEndDate: Date;
}

function Reservable({vin, index, licensePlateNumber, reservationPeriodStartDate, reservationPeriodEndDate}: ReservableProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const inspectionDateString = `${CldrDatetimeFormatter.format(reservationPeriodStartDate, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(reservationPeriodEndDate, 'yMMMd')}`;

  return (
    <>
      <dl className="vehicle_info">
        <div className="vehicle_item">
          <dt className="info_title">{t('T30520')}</dt>
          <dd className="info_text">{licensePlateNumber}</dd>
        </div>

        <div className="vehicle_item">
          <dt className="info_title">{t('T30534')}</dt>
          <dd className="info_text">{inspectionDateString}</dd>
        </div>
      </dl>

      <div className="btn_box">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => {
            WebviewUtils.openNewWebView({
              url: window.location.origin + ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_RESERVATION_NEW.getFullPath(vin, index),
              isModal: true,
              isNavigationBar: false,
              isToolBar: false,
            });
          }}
        >
          {t('T30541')}
        </button>
      </div>
    </>
  );
}

interface NotReservableRegisteredDateProps {
  vin: string;
  index: number;
  licensePlateNumber: string;
  reservationPeriodStartDate: Date;
  reservationPeriodEndDate: Date;
}

function NotReservableRegisteredDate({
  vin,
  index,
  licensePlateNumber,
  reservationPeriodStartDate,
  reservationPeriodEndDate,
}: NotReservableRegisteredDateProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const inspectionDateString = `${CldrDatetimeFormatter.format(reservationPeriodStartDate, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(reservationPeriodEndDate, 'yMMMd')}`;
  return (
    <>
      <dl className="vehicle_info">
        <div className="vehicle_item">
          <dt className="info_title">{t('T30520')}</dt>
          <dd className="info_text">{licensePlateNumber}</dd>
        </div>
        <div className="vehicle_item">
          <dt className="info_title">{t('T30534')}</dt>
          <dd className="info_text">{inspectionDateString}</dd>
        </div>
      </dl>

      <div className="guide_box">
        <p className="desc">{t('T30538')}</p>
      </div>

      <div className="btn_box">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() =>
            WebviewUtils.openNewWebView({
              url: window.location.origin + ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_EDIT.getFullPath(vin, index),
              isModal: true,
              isNavigationBar: false,
              isToolBar: false,
            })
          }
        >
          {t('T30527')}
        </button>
      </div>
    </>
  );
}

interface NotReservableNotRegisteredDateProps {
  licensePlateNumber: string;
  index: number;
  vin: string;
}

function NotReservableNotRegisteredDate({licensePlateNumber, vin, index}: NotReservableNotRegisteredDateProps) {
  const {t} = useInferableTranslation();
  return (
    <>
      <dl className="vehicle_info">
        <div className="vehicle_item">
          <dt className="info_title">{t('T30520')}</dt>
          <dd className="info_text">{licensePlateNumber}</dd>
        </div>
      </dl>

      <div className="guide_box">
        <p className="desc">{t('T30539')}</p>
      </div>

      <div className="btn_box">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() =>
            WebviewUtils.openNewWebView({
              url: window.location.origin + ROUTE_PATHS.PUBLIC_SERVICE_VEHICLE_INSPECTION_EDIT.getFullPath(vin, index),
              isModal: true,
              isNavigationBar: false,
              isToolBar: false,
            })
          }
        >
          {t('T30540')}
        </button>
      </div>
    </>
  );
}

function Loading() {
  return (
    <li className="application_item skeleton">
      <a
        className="link"
        role="button"
      >
        <div className="application_text">
          <strong className="title">&nbsp;</strong>
          <p className="desc">&nbsp;</p>
        </div>
        <span className="ic ic_arrow_forward_16px"></span>
      </a>
    </li>
  );
}
